import { SignalListeners, VisualizationSpec } from "react-vega";
import { Actions } from "vega-embed";
import { ByReceiverAbacusRow } from "../../redux/detections/detection-types";
import { isTimeWithinBounds } from "../../helpers/time";
import { VegaAutosized } from "../common/charts/VegaAutosized";
import { LOADING_INDICATOR_POSITION } from "./detection-consts";
import { useEffect, useState } from "react";
import { Button } from "@material-ui/core";
import { DataBounds, IntervalUpdate, TimeUnitType } from "./DetectionVisualizer";
import { debounce, max, min } from "lodash";
import { useThunkDispatch } from "../../redux/common";

import { getBinInfo } from "../../containers/Detections";
import { DetectionFilterInput } from "../../redux/detections/detection-types";
import { queryAbacus } from "../../redux/detections/detection-actions";
import { MINUTE_IN_MS, HOUR_IN_MS } from "../../helpers/time";

type PropTypes = {
  byRxData: ByReceiverAbacusRow[];
  interval: IntervalUpdate;
  actions?: Actions;
  loading: boolean;
  setZoomWindow: (interval: IntervalUpdate) => void;
  timeUnit: {
    unit: TimeUnitType;
    step: number;
  };
  setAbacusBinSeconds: (number) => void;
  currentFilters: DetectionFilterInput;
  histogramReady: boolean;
  dataBounds: DataBounds;
};

const Y_AXIS_EXTENT = 50;
const TIME_UNIT_MS = {
  yearmonthdate: HOUR_IN_MS * 24, // day
  yearmonthdatehours: HOUR_IN_MS, // hour
  yearmonthdatehoursminutes: MINUTE_IN_MS, // minute
};

function DetectionAbacus({
  byRxData,
  interval,
  actions,
  loading,
  timeUnit,
  setZoomWindow,
  setAbacusBinSeconds,
  currentFilters,
  histogramReady,
  dataBounds,
}: PropTypes) {
  const [zoom, setZoom] = useState<IntervalUpdate>([]); // the interval as time
  const [brushX, setBrushX] = useState(0); // the horizontal start of the interval as pixel offset from chart container

  // interval changed, resetting zoom:
  useEffect(() => {
    setZoom([]);
  }, [interval, setZoom]);

  const dispatch = useThunkDispatch();
  const xScale = { domain: [] as number[], nice: false };
  let filteredData = byRxData;
  const rxDomain = [...new Set(byRxData.map(rx => rx.rx))].sort();
  /** timestamp to mark end of data */
  let dtLastBin = 0;
  /** timestamp to mark beginning of data */
  let dtFirstBin = 0;

  if (interval?.length == 2) {
    const start = new Date(interval[0]).toISOString();
    const end = new Date(interval[1]).toISOString();
    filteredData = byRxData.filter(({ dt: time }) => isTimeWithinBounds({ time, start, end }));
    xScale.domain = [...interval];

    if (filteredData.length > 1) {
      const binWidth = TIME_UNIT_MS[timeUnit.unit] * timeUnit.step;
      // prepare a marker for the end of the data:
      if (interval[1] >= dataBounds.dtLast) {
        // so it's the timestamp of the last data bin plus enough to put it in the next bin:
        dtLastBin = new Date(filteredData[filteredData.length - 1].dt).valueOf() + binWidth;
      }

      // prepare a marker for the beginning of the data:
      if (interval[0] <= dataBounds.dtFirst) {
        // so it's the timestamp of the last data bin plus enough to put it in the next bin:
        dtFirstBin = new Date(filteredData[0].dt).valueOf() - binWidth;
      }
    }
  } else {
    filteredData = byRxData;
    xScale.domain = [dataBounds.dtFirst, dataBounds.dtLast];
  }

  useEffect(() => {
    if (histogramReady && (interval?.length === 2 || dataBounds.dtLast > dataBounds.dtFirst)) {
      // start based on selection interval, constrained by the time extents of the overview plot:
      const start = max([interval?.[0], dataBounds?.dtFirst || 0]) as number; // TS didn't realize this would always resolve to a number
      const end = min([interval?.[1], dataBounds?.dtLast || 0]) as number;
      const timeDifference = end - start;

      if (timeDifference > 0) {
        const filters: DetectionFilterInput = {
          includeReceiverSerials: currentFilters.includeReceiverSerials,
          includeTransmitterIDs: currentFilters.includeTransmitterIDs,
          includeStartTime: new Date(start),
          includeEndTime: new Date(end),
        };
        const adjustedBin = getBinInfo(timeDifference / 1000, 200);
        const queryAbacusDebouncedInner = debounce(
          (filters, binSeconds, dispatch, setAbacusBinSeconds) =>
            dispatch(queryAbacus(filters, binSeconds)).then(() => setAbacusBinSeconds(binSeconds)),
          300
        );
        const queryAbacusDebounced = (filters, binSeconds, dispatch, setAbacusBinSeconds) =>
          queryAbacusDebouncedInner(filters, binSeconds, dispatch, setAbacusBinSeconds);
        queryAbacusDebounced(filters, adjustedBin, dispatch, setAbacusBinSeconds);
      }
    }
  }, [
    dispatch,
    interval,
    dataBounds.dtFirst,
    dataBounds.dtLast,
    currentFilters,
    histogramReady,
    setAbacusBinSeconds,
  ]);

  const spec: VisualizationSpec = {
    usermeta: { embedOptions: { renderer: "svg" } },
    params: [
      {
        name: "brushOffset",
        expr: "brush_dt && scale('x', brush_dt[0])",
      },
    ],
    layer: [
      {
        data: { name: "byRx" },
        params: [
          {
            name: "brush",
            select: {
              type: "interval",
              encodings: ["x"],
              mark: {
                stroke: "black",
                strokeWidth: 2,
              },
            },
          },
        ],
        mark: "rect",
        encoding: {
          x: {
            field: "dt",
            type: "temporal",
            timeUnit: {
              binned: true,
              utc: true,
              unit: timeUnit.unit,
              step: timeUnit.step,
            },
            axis: {
              title: "",
              bandPosition: 0,
              labelBound: true, // this prevents the axis labels from exceeding the chart container
            },
            scale: xScale,
          },
          color: {
            field: "count",
            type: "quantitative",
            title: "Detections",
            legend: { orient: "bottom" },
          },
          y: {
            field: "rx",
            type: "nominal",
            axis: {
              title: "Receiver",
              minExtent: Y_AXIS_EXTENT,
            },
            scale: { domain: rxDomain },
          },
        },
      },
      {
        transform: [
          {
            filter: "datum.dtLastBin > 0 && datum.dtIntervalEnd >= datum.dtLastBin",
          },
        ],
        data: { values: [{ dtLastBin, dtIntervalEnd: interval?.[1] || 0 }] },
        mark: { type: "rule", size: 4, color: "red" },
        encoding: {
          x: {
            type: "temporal",
            field: "dtLastBin",
            scale: xScale,
          },
        },
      },
      {
        transform: [
          {
            filter: "datum.dtFirstBin > 0 && datum.dtIntervalStart <= datum.dtFirstBin",
          },
        ],
        data: { values: [{ dtFirstBin, dtIntervalStart: interval?.[0] || 0 }] },
        mark: { type: "rule", size: 4, color: "red" },
        encoding: {
          x: {
            type: "temporal",
            field: "dtFirstBin",
            scale: xScale,
          },
        },
      },
    ],
  };

  const signalListeners: SignalListeners = {
    brush: (_, { dt }) => setZoom(dt as IntervalUpdate),
    brushOffset: (_, brushOffset: number) => setBrushX(brushOffset),
  };
  const showZoomButton = zoom?.constructor === Array && zoom.length === 2;

  return (
    <>
      <VegaAutosized
        spec={spec}
        data={{ byRx: filteredData }}
        signalListeners={signalListeners}
        actions={actions}
        id="abacus"
        fit="width"
        loading={loading}
        loaderPosition={LOADING_INDICATOR_POSITION}
      />
      {showZoomButton && (
        <Button
          variant="contained"
          style={{
            position: "absolute",
            left: (brushX || 0) + Y_AXIS_EXTENT,
            top: 5,
            transform: "scale(0.75)",
          }}
          onClick={() => {
            setZoomWindow([...zoom]);
            setZoom([]);
          }}
        >
          zoom to selection
        </Button>
      )}
    </>
  );
}

export default DetectionAbacus;
